import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError, isIn } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import {
  ContentSeries,
  Course,
  CourseFormat,
  MaterialType,
} from '@/client/courses';
import { Actions, Subjects } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { ContentBrowseView } from '@/components/courses/browse';
import { ContentViewToggle } from '@/components/courses/ContentViewToggle';
import { CourseCloneStatusStack } from '@/components/courses/CourseCloneStatusStack';
import { useCourseRowMenu } from '@/components/courses/datatables/useCourseRowMenu';
import { useCoursesColumns } from '@/components/courses/datatables/useCoursesColumns';
import { HighlightedCoursesRow } from '@/components/courses/HighlightedCoursesRow';
import { CloneCourseModal } from '@/components/courses/modals/CloneCourseModal';
import { CoursePreviewOverlay } from '@/components/courses/preview/CoursePreviewOverlay';
import { ViewOnlyRestriction } from '@/components/custom-roles';
import {
  DataTable,
  DataTableColumnsMultiselect,
  DataTableFilters,
  DataTableToolbar,
} from '@/components/tables/crud';
import {
  useCourses,
  useDeleteCourse,
  useHighlightedCourses,
  useSaveCourse,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useContentViewMode } from '@/hooks/useContentViewMode';
import { useCourseClone } from '@/hooks/useCourseClone';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { branchAdminCheck, isDemoRestrictedMode } from '@/utils/helpers';
import { coursesFilters } from '@/utils/helpers/courses-filters.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const CoursesPage = () => {
  const { t } = useTranslation();
  const { can, isRestrictedByRole } = usePermission();
  const isCreateRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.COURSES,
  );
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);
  const isBranchAdmin = branchAdminCheck(currentUser, account);
  const {
    dialogCourse: cloneDialogCourse,
    cloningCourseIds,
    cloneJobs,
    openCloneDialog,
    closeCloneDialog,
    submitClone,
    removeCloneJob,
  } = useCourseClone();
  const deleteCourse = useDeleteCourse();
  const navigate = useNavigate();
  const { accountCanUseFeature } = useFeatureFlag();

  const { skip, take, sort, apiFilters, onSort, setSkip, onPage, onFilter } =
    useTable({
      defaultSort: ['created', 'asc'],
      tableName: TableNamesEnum.COURSES_LIST,
    });

  const canUseBrowseView =
    !!account?.isSystem ||
    accountCanUseFeature(
      PostHogFeatureFlags.CONTENT_PAGE_BROWSE_VIEW,
      account?.featureFlagAbility ?? [],
    );
  const isBrowseViewDefault = accountCanUseFeature(
    PostHogFeatureFlags.CONTENT_PAGE_BROWSE_DEFAULT,
    account?.featureFlagAbility ?? [],
  );

  const { contentViewMode, handleContentViewChange, isBrowseView } =
    useContentViewMode({ canUseBrowseView, isBrowseViewDefault });

  const [previewCourseId, setPreviewCourseId] = useState<string | null>(null);
  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 500),
    [setSkip, setMultiSearchValue],
  );

  const { isLoading, courses } = useCourses({
    accountId: account?.isSystem ? undefined : (account?.id as string),
    take,
    skip,
    search: multiSearchValue,
    filters: [
      ...apiFilters,
      isIn('type', [MaterialType.COURSE, MaterialType.CUSTOM_MATERIAL]),
    ],
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
    enabled: !isBrowseView,
  });

  const menuItems = useCourseRowMenu({
    onDelete: (course) => handleDeleteCourse(course),
    onClone: (course) => openCloneDialog(course),
    onSubmitClone: (targetAccountId, targetAccountName, course) =>
      submitClone(targetAccountId, targetAccountName, course),
  });

  const handleDeleteCourse = async (course: Course) => {
    if (isDemoAccount) return;
    try {
      await deleteCourse.delete(course.id);
      toast?.success(t('toast.success'), t('content.deleted'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const columns = useCoursesColumns({ menuItems, cloningCourseIds });

  const defaultVisibleColumns = columns
    .filter((column) => column.field !== 'id')
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    defaultVisibleColumns,
  );

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        {!!coursesFilters(t).length && (
          <DataTableFilters
            filters={coursesFilters(t)}
            onFilter={onFilter}
            tableName={TableNamesEnum.COURSES_LIST}
            defaultValues={{
              languages: [],
              contentSeries: [
                ContentSeries.ESSENTIALS,
                ContentSeries.STORIES,
                ContentSeries.THREAT_ALERTS,
                null,
              ],
            }}
            className="flex-initial"
          />
        )}
        <div className="p-input-icon-left flex-auto min-w-300">
          <InputText
            className="w-full"
            onInput={debouncedSetMultiSearchValue}
            placeholder={t('content.search.name')}
            autoComplete="off"
          />
          <i className="pi pi-search" />
        </div>
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.COURSES_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const pathItems = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('content'),
      url: RedirectPaths[RedirectPathsEnum.COURSES](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const onRowClick = (e: DataTableRowClickEvent) => {
    // The overlay opens for every course and gates playback itself for
    // non-previewable (e.g. future-release) content, so the entry point is
    // consistent regardless of previewability.
    setPreviewCourseId(e.data?.id ?? null);
  };

  const { highlightedCourses, isLoading: isHighlightedCoursesLoading } =
    useHighlightedCourses(account?.id, {
      enabled: !!account?.id && !isBrowseView,
      onError: (e: unknown) => handleAxiosError(e as Error | AxiosError, toast),
    });

  const createCourse = useSaveCourse();
  const handleCreateCourse = async () => {
    const response = await createCourse.create({
      format: CourseFormat.CONTENTS,
      type: MaterialType.COURSE,
      courseTranslations: [
        {
          name: t('content.newContent'),
          language: LanguagesEnum.EN,
          description: '',
        },
      ],
      courseLanguages: [LanguagesEnum.EN],
      account: !account?.isSystem && account?.id ? account.id : null,
      courseFiles: [],
      releaseDate: null,
      plannerScheduleDate: null,
      isPreviewable: false,
    });
    navigate(RedirectPaths[RedirectPathsEnum.COURSES_CREATE](response.id));
  };

  const canCreateContent =
    can(Actions.CREATE, Subjects.COURSES) &&
    (account?.isSystem || !isBranchAdmin);

  const showCreateContent =
    canCreateContent ||
    (isCreateRestricted && (account?.isSystem || !isBranchAdmin));

  const createContentButton =
    showCreateContent &&
    (isCreateRestricted ? (
      <ViewOnlyRestriction>
        <AppButton
          label={t('button.createNew')}
          severity="secondary"
          isDisabled
        />
      </ViewOnlyRestriction>
    ) : (
      <AppButton
        label={t('button.createNew')}
        severity="secondary"
        onClick={isDemoAccount ? undefined : handleCreateCourse}
      />
    ));

  const manageView = isHighlightedCoursesLoading ? (
    <FlexContainer direction="column" className="mt-5">
      <ProgressSpinner />
    </FlexContainer>
  ) : (
    <>
      <HighlightedCoursesRow
        courses={highlightedCourses}
        onCourseSelect={setPreviewCourseId}
      />

      <DataTable
        data={courses?.result}
        count={courses?.count as number}
        isLoading={isLoading}
        toolbar={toolbar}
        columns={columns}
        visibleColumns={visibleColumns}
        onPage={onPage}
        rows={take}
        skip={skip}
        onSort={onSort}
        sort={sort}
        tableClassName="content-table"
        onRowClick={onRowClick}
      />
    </>
  );

  return (
    <>
      <AppBreadCrumb model={pathItems} />
      <FlexContainer justify="space-between" className="mb-4">
        <h1 className="mb-0">{t('content')}</h1>
        {canUseBrowseView ? (
          <FlexContainer width="auto" gap="var(--default-padding-small)">
            <ContentViewToggle
              value={contentViewMode}
              onChange={handleContentViewChange}
            />
            {createContentButton}
          </FlexContainer>
        ) : (
          createContentButton
        )}
      </FlexContainer>

      {isBrowseView ? (
        <ContentBrowseView
          accountId={account?.isSystem ? undefined : account?.id}
          onCourseSelect={setPreviewCourseId}
          onCreateNewContent={handleCreateCourse}
          canCreateContent={canCreateContent && !isDemoAccount}
          isCreateRestricted={isCreateRestricted}
        />
      ) : (
        manageView
      )}

      <CoursePreviewOverlay
        courseId={previewCourseId}
        onHide={() => setPreviewCourseId(null)}
        onCustomizeManually={(course) => {
          setPreviewCourseId(null);
          openCloneDialog(course);
        }}
      />

      <CloneCourseModal
        visible={!!cloneDialogCourse}
        course={cloneDialogCourse}
        currentAccount={account}
        isCloning={
          !!cloneDialogCourse && cloningCourseIds.includes(cloneDialogCourse.id)
        }
        onHide={closeCloneDialog}
        onClone={submitClone}
      />

      <CourseCloneStatusStack jobs={cloneJobs} onRemove={removeCloneJob} />
    </>
  );
};
